import { FormControl, FormLabel, Text, Textarea } from "@chakra-ui/react";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NodeProps } from "reactflow";
import { useUpdateNodeData } from "../../../hooks/useUpdateNodeData";
import { NodeData, NodeType } from "../../../models/nodeType";
import SelectGui from "../../base/SelectGui";
import { FlowNodeWithChildren } from "./FlowNode";

interface OpenUIElement extends NodeData {
  uiElement: string;
  initParams?: string;
}

const OpenUIElementNode: React.FC<NodeProps<NodeType<OpenUIElement>>> = (props) => {
  const {
    id: nodeId,
    data: { color, nodeData },
  } = props;

  const uiElement = nodeData?.uiElement ?? "";
  const initParams = nodeData?.initParams ?? "";

  const [jsonError, setJsonError] = useState<string | null>(null);

  const { getValues, setValue, handleSubmit, register, watch } = useForm<OpenUIElement>({
    defaultValues: useMemo(
      () => ({
        uiElement,
        initParams,
      }),
      [uiElement, initParams]
    ),
    mode: "onBlur",
  });

  const { updateNodeData } = useUpdateNodeData<OpenUIElement>(nodeId);

  useEffect(() => {
    const initParams = watch("initParams");

    if (!initParams) {
      setJsonError(null);
      return;
    }

    try {
      JSON.parse(initParams);
      setJsonError(null);
    } catch (error) {
      setJsonError("Invalid JSON format");
    }
  }, [watch("initParams")]);

  return (
    <FlowNodeWithChildren {...props}>
      <form className={"nodrag"} onSubmit={handleSubmit(updateNodeData)} onBlur={handleSubmit(updateNodeData)}>
        <SelectGui color={color} value={getValues("uiElement")} setValue={(value) => setValue("uiElement", value)} />
        <FormControl>
          <FormLabel color={color} textTransform={"uppercase"}>
            Init Params (optional)
          </FormLabel>
          <Textarea {...register("initParams")} color={color} isInvalid={!!jsonError} />
          {jsonError && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {jsonError}
            </Text>
          )}
        </FormControl>
      </form>
    </FlowNodeWithChildren>
  );
};

export default memo(OpenUIElementNode);
